// ============================================================================
// Going out (from the Prime app's old Overview, 2026-10-10): prod and staging's
// versions, a ship on its way as a pipeline, what waits for prod with Ship all,
// and the latest builds, with the Pipeline's lanes before prod in brief
// (components/Pipeline.tsx). Under the list on DevOS's Now page (WorkDay.tsx).
// Online-only: `/api/builds` through useBuilds.
// ============================================================================
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useNow } from '@/hooks/useNow'
import { fmtAgo } from '@/lib/time'
import { pipelineOf, runsVersion, shipping } from '@/lib/pipeline'
import { ShipSheet } from '@/views/dev/Releases'
import { Skeleton } from './Skeleton'
import { PipelineCompact } from './Pipeline'
import { ShipPipeline, useBuilds } from './ShipPipeline'
import { Button, SectionTitle, Tag } from './ui'

export const GoingOut = () => {
  const now = useNow(30_000)
  const { data, error, unreachable, reload } = useBuilds()
  const [ship, setShip] = useState(false)
  if (!data)
    return (
      <section className="mb-5">
        <SectionTitle>Going out</SectionTitle>
        {error ? <p className="text-sm text-muted px-1">{error}</p> : <Skeleton className="h-28" />}
      </section>
    )
  // a ship on its way covers its build and every older one: no second Ship for those
  const onWay = shipping(data) ? (data.releases[0]?.sha ?? '') : ''
  const onWayAt = onWay ? data.builds.findIndex((b) => runsVersion(onWay, b.tag)) : -1
  const outAt = data.outgoing ? data.builds.findIndex((b) => b.name === data.outgoing?.build.name) : -1
  const out = data.outgoing && !(onWayAt >= 0 && outAt >= onWayAt) ? data.outgoing : undefined
  const latest = data.builds.slice(0, 3)
  return (
    <section className="mb-5" data-testid="overview-out">
      <SectionTitle
        right={
          <Link to="/dev/releases" className="text-xs text-muted">
            Releases
          </Link>
        }
      >
        Going out
      </SectionTitle>
      <ul className="group-list mb-2">
        {(['prod', 'staging'] as const).map((env) => (
          <li key={env} className="flex items-center justify-between px-3 min-h-11">
            <span className="capitalize">{env}</span>
            <span className="font-mono text-sm text-muted">{data.versions[env] || 'not answering'}</span>
          </li>
        ))}
      </ul>
      {pipelineOf(data, now, unreachable) && (
        <div className="mb-2">
          <ShipPipeline builds={data} unreachable={unreachable} />
        </div>
      )}
      {/* what is on its way to that: ready, queued, merging, building, waiting, and what is stuck */}
      <PipelineCompact />

      {out ? (
        <div className="card p-3 mb-2">
          <p className="label mb-1.5">Waiting for prod</p>
          <ul className="flex flex-col gap-1 mb-3">
            {(out.commits.length
              ? out.commits
              : [{ sha: out.build.sha, subject: out.build.note || out.build.tag }]
            ).map((c) => (
              <li key={c.sha} className="text-sm leading-5">
                {c.subject}
              </li>
            ))}
          </ul>
          <Button
            variant="primary"
            big
            className="w-full"
            onClick={() => setShip(true)}
            data-testid="overview-ship"
          >
            Ship all to prod
          </Button>
        </div>
      ) : onWay ? null : (
        <p className="text-sm text-muted px-1 mb-2">Prod runs the newest build.</p>
      )}
      {latest.length > 0 && (
        <ul className="group-list">
          {latest.map((b) => (
            <li key={b.name} className="px-3 py-2 min-h-11">
              <span className="block text-sm leading-5 line-clamp-1">{b.note || b.tag}</span>
              <span className="flex items-center gap-1.5 mt-0.5">
                <Tag tone={b.state === 'built' ? 'done' : b.state === 'failed' ? 'warn' : 'running'}>
                  {b.state}
                </Tag>
                <span className="font-mono text-[11px] text-faint">{b.tag}</span>
                <span className="text-[11px] text-faint">{fmtAgo(b.finished || b.started, now)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {ship && out && (
        <ShipSheet
          sha={out.build.tag}
          note={out.commits[0]?.subject || out.build.note}
          back={false}
          onClose={() => setShip(false)}
          onDone={() => {
            setShip(false)
            reload()
          }}
        />
      )}
    </section>
  )
}
